import React from 'react';

/**
 * KD-3581 §3, level 2 — one card per day inside an experience frame.
 *
 * Program on top, accommodation below: the two are stacked and never side by
 * side. The panel keeps its current width, and two dense cards on one row clip
 * or wrap at unwanted places; stacking gives each of them the full width.
 *
 * The three header parts are already formatted by the app, which owns i18n and
 * the date library — this component only decides how they are separated. They
 * sit on a shared baseline at three decreasing sizes, so "Day 3" stays the
 * anchor of the card while the weekday and the date read as its qualifiers.
 */
type TripDayCardProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className'
> & {
  /** "Day 3" — the only part that is always present. */
  dayLabel: string;
  weekdayLabel?: string;
  dateLabel?: string;
  program?: React.ReactNode;
  accommodation?: React.ReactNode;
};

const TripDayCard = React.forwardRef<HTMLDivElement, TripDayCardProps>(
  (
    { dayLabel, weekdayLabel, dateLabel, program, accommodation, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className="rounded-xl border border-solid border-[#E7ECF3] bg-white p-3.5"
      {...props}
    >
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 pb-3">
        <span className="text-base font-bold text-[#16284F]">{dayLabel}</span>

        {weekdayLabel && (
          <span className="text-[15px] text-slate-500">· {weekdayLabel}</span>
        )}

        {dateLabel && (
          <span className="text-[13.5px] text-slate-500">{dateLabel}</span>
        )}
      </div>

      <div className="flex flex-col gap-2.5">
        {program}
        {accommodation}
      </div>
    </div>
  ),
);

TripDayCard.displayName = 'TripDayCard';

export default TripDayCard;
